
import { uiText } from '@/i18n/uiText'
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { ElMessage } from 'element-plus'
import i18n from '@/i18n'
import { useAuthStore } from '@/stores/auth'
import { claimRouteReload, clearRouteReload, isDynamicRouteLoadError } from '@/utils/routeLoadRecovery'
import { canOpenSettings, legacyInstallLocation } from '@/views/settings/settingsAccess'

declare module 'vue-router' { interface RouteMeta { public?: boolean; permission?: string; superAdminOnly?: boolean; settingsEntry?:boolean } }

const LoginPage = () => import('@/views/LoginView.vue')
const RemoteConsolePage = () => import('@/views/RemoteConsolePage.vue')
const AppLayout = () => import('@/layouts/AppLayout.vue')
const OverviewPage = () => import('@/views/OverviewView.vue')
const NotFoundPage = () => import('@/views/NotFoundView.vue')
const DevicesPage = () => import('@/views/DevicesView.vue')
const CustomersPage = () => import('@/views/CustomersView.vue')
const DatacentersPage = () => import('@/views/DatacentersView.vue')
const RacksPage = () => import('@/views/RacksView.vue')
const SwitchesPage = () => import('@/views/SwitchesView.vue')
const SwitchDetailPage = () => import('@/views/SwitchDetailView.vue')
const IPAMPage = () => import('@/views/IPAMView.vue')
const UsersPage = () => import('@/views/UsersView.vue')
const AgentsPage = () => import('@/views/AgentsView.vue')
const DiscoveryPage = () => import('@/views/DiscoveryView.vue')
const StoragePage = () => import('@/views/StorageView.vue')
const ImageManagementPage = () => import('@/views/ImageManagementView.vue')
const ProvisioningPage = () => import('@/views/ProvisioningView.vue')
const RemoteSessionsPage = () => import('@/views/RemoteSessionsView.vue')
const LifecyclePage = () => import('@/views/LifecycleView.vue')
const AuditLogsPage = () => import('@/views/AuditLogsView.vue')

const routes: RouteRecordRaw[] = [
  { path: '/login', name: 'login', component: LoginPage, meta: { public: true } },
  { path: '/remote-console/:deviceId', name: 'remote-console', component: RemoteConsolePage, meta: { permission: 'device:read' } },
  { path: '/switches/:id/terminal', name: 'switch-terminal', component: () => import('@/views/SwitchTerminalPage.vue'), meta: { permission: 'network:terminal' } },
  {
    path: '/', component: AppLayout,
    children: [
      { path: '', name: 'overview', component: OverviewPage },
      { path: 'tenants', redirect: '/' },
      { path: 'customers', name: 'customers', component: CustomersPage, meta: { permission: 'customer:read' } },
      { path: 'sales/groups', name: 'sales-groups', component: () => import('@/views/SalesGroupsView.vue'), meta: { permission: 'device:read' } },
      { path: 'sales/finance', name: 'sales-finance', component: () => import('@/views/FinanceSettingsView.vue'), meta: { permission: 'device:update' } },
      { path: 'datacenters', name: 'datacenters', component: DatacentersPage, meta: { permission: 'datacenter:read' } },
      { path: 'racks', name: 'racks', component: RacksPage, meta: { permission: 'rack:read' } },
      { path: 'devices', name: 'devices', component: DevicesPage, meta: { permission: 'device:read' } },
      { path: 'devices/:id', name: 'device-detail', component: DevicesPage, meta: { permission: 'device:read' } },
      { path: 'switches', name: 'switches', component: SwitchesPage, meta: { permission: 'network:read' } },
      { path: 'switches/:id', name: 'switch-detail', component: SwitchDetailPage, meta: { permission: 'network:read' } },
      { path: 'ipam', name: 'ipam', component: IPAMPage, meta: { permission: 'ipam:read' } },
      { path: 'ipam/devices/:id', name: 'device-ip-management', component: () => import('@/views/DeviceIPManagementView.vue'), meta: { permission: 'ipam:read' } },
      { path: 'users', name: 'users', component: UsersPage, meta: { permission: 'user:read' } },
      { path: 'agents', name: 'agents', component: AgentsPage, meta: { permission: 'agent:read' } },
      { path: 'remote-sessions', name: 'remote-sessions', component: RemoteSessionsPage, meta: { permission: 'remote:read' } },
      { path: 'discovery', name: 'discovery', component: DiscoveryPage, meta: { permission: 'discovery:read' } },
      { path: 'operations-tools', name: 'operations-tools', component: () => import('@/views/OperationsToolsView.vue'), meta: { permission: 'discovery:scan' } },
      { path: 'storage', name: 'storage', component: StoragePage, meta: { permission: 'storage:read' } },
      { path: 'provisioning/images', name: 'image-management', component: ImageManagementPage, meta: { permission: 'provisioning:image:read' } },
      { path: 'provisioning', name: 'provisioning', component: ProvisioningPage, meta: { permission: 'provisioning:install:read' } },
      { path: 'settings/install', name: 'install-settings', redirect: to => legacyInstallLocation(to.query) },
      { path: 'settings/system', name: 'system-settings', component: () => import('@/views/SystemSettingsView.vue'), meta: { settingsEntry: true } },
      { path: 'account/security', name: 'account-security', component: () => import('@/views/AccountSecurityView.vue') },
      { path: 'lifecycle', name: 'lifecycle', component: LifecyclePage, meta: { permission: 'lifecycle:read' } },
      { path: 'audit-logs', name: 'audit-logs', component: AuditLogsPage, meta: { permission: 'audit:read' } },
    ],
  },
  { path: '/:pathMatch(.*)*', component: NotFoundPage, meta: { public: true } },
]

const router = createRouter({ history: createWebHistory(), routes })
router.onError((error, to) => {
  if (!isDynamicRouteLoadError(error)) return
  if (claimRouteReload(sessionStorage, to.fullPath)) {
    window.location.assign(to.fullPath)
    return
  }
  ElMessage.error(uiText("页面资源已经更新，请按 Ctrl+F5 刷新后重试"))
})
router.beforeEach(async (to) => {
  const auth = useAuthStore()
  await auth.restore()
  if (to.meta.public) return to.name === 'login' && auth.authenticated ? { name: 'overview' } : true
  if (!auth.authenticated) return { name: 'login', query: { redirect: to.fullPath } }
  if (!auth.can(to.meta.permission)) {
    ElMessage.warning(i18n.global.t('error.FORBIDDEN'))
    return { name: 'overview' }
  }
  if (to.meta.superAdminOnly && !auth.isSuperAdmin) {
    ElMessage.warning(i18n.global.t('error.FORBIDDEN'))
    return { name: 'overview' }
  }
  if (to.meta.settingsEntry && !canOpenSettings(auth)) {
    ElMessage.warning(i18n.global.t('error.FORBIDDEN'))
    return { name:'overview' }
  }
  return true
})
router.afterEach((_to, _from, failure) => {
  if (!failure) clearRouteReload(sessionStorage)
})
export default router
